import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import {
  DollarSign,
  AlertTriangle,
  Clock,
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  Users,
  TrendingUp,
  X,
  FileText,
  Plus,
  Send,
  CreditCard,
  Ban,
  Printer
} from 'lucide-react';

interface AgingBucket {
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  days90plus: number;
  total: number;
}

interface PhysicianARSummary {
  physicianId: string;
  physicianName: string;
  physicianEmail: string;
  clinicName?: string;
  distributorId?: string;
  distributorName?: string;
  arEnabled: boolean;
  creditLimit: number | null;
  outstandingBalance: number;
  availableCredit: number | null;
  isOverLimit: boolean;
  aging: AgingBucket;
  invoiceCount: number;
  oldestOverdueDate?: string;
  paymentTerms: string;
}

interface ARSummaryResponse {
  summary: PhysicianARSummary[];
  totals: AgingBucket;
  physicianCount: number;
  overduePhysicianCount: number;
}

interface Distributor {
  _id: string;
  profile: { firstName: string; lastName: string };
  distributorData?: { companyName?: string };
}

const distributorLabel = (d: Distributor) =>
  d.distributorData?.companyName || `${d.profile.firstName} ${d.profile.lastName}`;

interface Statement {
  _id: string;
  statementNumber: string;
  periodStart: string;
  periodEnd: string;
  totalAmount: number;
  status: 'draft' | 'sent' | 'paid' | 'voided';
  dueDate: string;
  invoiceCount: number;
  createdAt: string;
  wireTransferNumber?: string;
  paidAt?: string;
}

interface StatementDetail extends Statement {
  physicianId: {
    _id: string;
    email: string;
    profile: { firstName: string; lastName: string; clinicName?: string };
    physicianData?: {
      clinicAddress?: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zipCode: string;
      };
    };
  };
  lineItems: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  shippingTotal: number;
  telemedNetworkFeeTotal?: number;
  taxTotal?: number;
  volumeRewardDiscount?: number;
  paymentTerms: string;
}

interface StatementPreview {
  physicianId: string;
  physicianName: string;
  clinicName?: string;
  existingInvoices: Array<{
    invoiceId: string;
    invoiceNumber: string;
    totalAmount: number;
    dueDate: string;
    status: string;
    orderCount: number;
  }>;
  orphanOrders: Array<{
    orderId: string;
    orderNumber: string;
    totalAmount: number;
    status: string;
    createdAt: string;
  }>;
  aggregatedLineItems: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  shippingTotal: number;
  telemedNetworkFeeTotal?: number;
  taxTotal?: number;
  totalAmount: number;
  invoiceCount: number;
  orderCount: number;
}


export const AdminAR: React.FC = () => {
  const user = useUser();
  const { notifyError } = useNotifications();

  // State
  const [loading, setLoading] = useState(true);
  const [arData, setArData] = useState<ARSummaryResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // The AM filter is a server-searched picker: there are more AMs than one
  // page holds, so a <select> filled by one fetch silently dropped most of them.
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const distributorFilter = selectedDistributor?._id ?? '';
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortField, setSortField] = useState<'outstandingBalance' | 'days90plus' | 'physicianName'>('outstandingBalance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Detail modal state
  const [selectedPhysician, setSelectedPhysician] = useState<PhysicianARSummary | null>(null);
  const [physicianStatements, setPhysicianStatements] = useState<Statement[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Statement preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [statementPreview, setStatementPreview] = useState<StatementPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creatingStatement, setCreatingStatement] = useState(false);

  // Date range filter for statement generation
  const [statementPeriodStart, setStatementPeriodStart] = useState('');
  const [statementPeriodEnd, setStatementPeriodEnd] = useState('');
  const [statementDateField, setStatementDateField] = useState<'invoiceDate' | 'shippedDate'>('invoiceDate');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatement, setPaymentStatement] = useState<Statement | null>(null);
  const [wireTransferNumber, setWireTransferNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Void modal state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidStatement, setVoidStatement] = useState<Statement | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidingStatement, setVoidingStatement] = useState(false);

  // Print state
  const [printStatement, setPrintStatement] = useState<StatementDetail | null>(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printStatementPending, setPrintStatementPending] = useState<Statement | null>(null);
  const [wireInstructions, setWireInstructions] = useState('');

  // Load data
  useEffect(() => {
    if (user?.role === 'admin') {
      loadARData();
    }
  }, [user, distributorFilter, overdueOnly]);

  const loadARData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (distributorFilter) params.append('distributorId', distributorFilter);
      if (overdueOnly) params.append('overdueOnly', 'true');

      const response = await apiClient.get<any>(`/ar/summary?${params.toString()}`);
      if (response.success) {
        setArData(response.data);
      } else {
        notifyError('Failed to Load', response.error || 'Could not load AR data');
      }
    } catch (error) {
      console.error('Error loading AR data:', error);
      notifyError('Error', 'Failed to load accounts receivable data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (distributorFilter) params.append('distributorId', distributorFilter);
      if (overdueOnly) params.append('overdueOnly', 'true');

      // Fetch CSV directly
      const response = await fetch(`/api/ar/export?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ar-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        notifyError('Export Failed', 'Could not export AR data');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      notifyError('Export Error', 'Failed to export AR data');
    }
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Load statements for selected physician
  const loadPhysicianStatements = async (physicianId: string) => {
    try {
      setDetailLoading(true);
      const response = await apiClient.getPhysicianStatements(physicianId);
      if (response.success && response.data) {
        setPhysicianStatements(response.data.statements || []);
      }
    } catch (error) {
      console.error('Error loading statements:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open detail modal
  const openDetailModal = (physician: PhysicianARSummary) => {
    setSelectedPhysician(physician);
    loadPhysicianStatements(physician.physicianId);
  };

  // Close detail modal
  const closeDetailModal = () => {
    setSelectedPhysician(null);
    setPhysicianStatements([]);
    setStatementPeriodStart('');
    setStatementPeriodEnd('');
    setStatementDateField('invoiceDate');
  };

  // Preview statement
  const handlePreviewStatement = async () => {
    if (!selectedPhysician) return;
    try {
      setPreviewLoading(true);
      const dateRange = (statementPeriodStart || statementPeriodEnd) ? {
        periodStart: statementPeriodStart || undefined,
        periodEnd: statementPeriodEnd || undefined,
        dateField: statementDateField
      } : undefined;
      const response = await apiClient.previewStatement(selectedPhysician.physicianId, dateRange);
      if (response.success && response.data) {
        setStatementPreview(response.data);
        setShowPreviewModal(true);
      } else {
        notifyError('Preview Failed', response.error || 'Could not preview statement');
      }
    } catch (error) {
      notifyError('Error', 'Failed to preview statement');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Create statement
  const handleCreateStatement = async () => {
    if (!selectedPhysician) return;
    try {
      setCreatingStatement(true);
      const response = await apiClient.createStatement({
        physicianId: selectedPhysician.physicianId,
        periodStart: statementPeriodStart || undefined,
        periodEnd: statementPeriodEnd || undefined,
        dateField: (statementPeriodStart || statementPeriodEnd) ? statementDateField : undefined
      });
      if (response.success) {
        setShowPreviewModal(false);
        setStatementPreview(null);
        loadPhysicianStatements(selectedPhysician.physicianId);
        loadARData(); // Refresh AR data
        notifySuccess('Statement Created', 'Consolidated statement has been created');
      } else {
        notifyError('Creation Failed', response.error || 'Could not create statement');
      }
    } catch (error) {
      notifyError('Error', 'Failed to create statement');
    } finally {
      setCreatingStatement(false);
    }
  };

  // Send statement
  const handleSendStatement = async (statement: Statement) => {
    try {
      const response = await apiClient.sendStatement(statement._id);
      if (response.success) {
        loadPhysicianStatements(selectedPhysician!.physicianId);
        notifySuccess('Statement Sent', 'Statement marked as sent');
      } else {
        notifyError('Send Failed', response.error || 'Could not send statement');
      }
    } catch (error) {
      notifyError('Error', 'Failed to send statement');
    }
  };

  // Open payment modal
  const openPaymentModal = (statement: Statement) => {
    setPaymentStatement(statement);
    setWireTransferNumber('');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  // Record payment
  const handleRecordPayment = async () => {
    if (!paymentStatement) return;
    try {
      setRecordingPayment(true);
      const response = await apiClient.recordStatementPayment(paymentStatement._id, {
        wireTransferNumber: wireTransferNumber || undefined,
        paymentNotes: paymentNotes || undefined
      });
      if (response.success) {
        setShowPaymentModal(false);
        setPaymentStatement(null);
        loadPhysicianStatements(selectedPhysician!.physicianId);
        loadARData(); // Refresh AR data
        notifySuccess('Payment Recorded', 'Statement and invoices marked as paid');
      } else {
        notifyError('Payment Failed', response.error || 'Could not record payment');
      }
    } catch (error) {
      notifyError('Error', 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  // Open void modal
  const openVoidModal = (statement: Statement) => {
    setVoidStatement(statement);
    setVoidReason('');
    setShowVoidModal(true);
  };

  // Void statement
  const handleVoidStatement = async () => {
    if (!voidStatement || !voidReason) return;
    try {
      setVoidingStatement(true);
      const response = await apiClient.voidStatement(voidStatement._id, voidReason);
      if (response.success) {
        setShowVoidModal(false);
        setVoidStatement(null);
        loadPhysicianStatements(selectedPhysician!.physicianId);
        loadARData(); // Refresh AR data
        notifySuccess('Statement Voided', 'Statement has been voided and invoices unlinked');
      } else {
        notifyError('Void Failed', response.error || 'Could not void statement');
      }
    } catch (error) {
      notifyError('Error', 'Failed to void statement');
    } finally {
      setVoidingStatement(false);
    }
  };

  // Print statement - show modal first to enter wire instructions
  const handlePrintStatement = (statement: Statement) => {
    setPrintStatementPending(statement);
    setShowPrintModal(true);
  };

  // Execute print after user enters wire instructions
  const executePrint = async () => {
    if (!printStatementPending) return;

    try {
      setPrintLoading(true);
      const response = await apiClient.getStatement(printStatementPending._id);
      if (response.success && response.data) {
        setPrintStatement(response.data);
        setShowPrintModal(false);
        // Wait for state to update, then trigger print
        setTimeout(() => {
          window.print();
        }, 100);
      } else {
        notifyError('Print Failed', response.error || 'Could not load statement details');
      }
    } catch (error) {
      notifyError('Error', 'Failed to load statement for printing');
    } finally {
      setPrintLoading(false);
    }
  };

  const { notifySuccess } = useNotifications();

  // Filter data by search term
  const filteredData = arData?.summary
    .filter(p => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        p.physicianName.toLowerCase().includes(search) ||
        p.physicianEmail.toLowerCase().includes(search) ||
        p.clinicName?.toLowerCase().includes(search) ||
        p.distributorName?.toLowerCase().includes(search)
      );
    }) || [];

  // Sort function
  const sortData = (data: PhysicianARSummary[]) => {
    return [...data].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'outstandingBalance':
          comparison = a.outstandingBalance - b.outstandingBalance;
          break;
        case 'days90plus':
          comparison = a.aging.days90plus - b.aging.days90plus;
          break;
        case 'physicianName':
          comparison = a.physicianName.localeCompare(b.physicianName);
          break;
      }
      return sortDir === 'desc' ? -comparison : comparison;
    });
  };

  // Split into two groups:
  // 1. AR Enabled accounts (regardless of balance)
  // 2. Outstanding balances (accounts with balance > 0, regardless of AR status)
  const arEnabledAccounts = sortData(filteredData.filter(p => p.arEnabled));
  const outstandingBalanceAccounts = sortData(filteredData.filter(p => p.outstandingBalance > 0));

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 print:bg-white print:min-h-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Accounts Receivable</h1>
          <p className="text-gray-600 mt-2">Track outstanding balances and aging by physician</p>
        </div>

        {/* Summary Cards */}
        {arData && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Current</p>
                  <p className="text-lg font-bold text-gray-900">{formatCurrency(arData.totals.current)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">1-30 Days</p>
                  <p className="text-lg font-bold text-yellow-600">{formatCurrency(arData.totals.days1to30)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-orange-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">31-60 Days</p>
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(arData.totals.days31to60)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">61-90 Days</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(arData.totals.days61to90)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-700" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">90+ Days</p>
                  <p className="text-lg font-bold text-red-700">{formatCurrency(arData.totals.days90plus)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-indigo-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Total AR</p>
                  <p className="text-lg font-bold text-indigo-600">{formatCurrency(arData.totals.total)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        {arData && (
          <div className="flex items-center gap-6 mb-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{arData.physicianCount} physicians with outstanding balance</span>
            </div>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" />
              <span>{arData.overduePhysicianCount} with overdue invoices</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search physicians..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Distributor Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <div className="w-64">
                <AsyncSearchSelect<Distributor>
                  fetchPage={({ page, pageSize, search, signal }) =>
                    apiClient.searchUsersPage({ role: 'distributor', page, pageSize, search, signal })}
                  getKey={d => String(d._id)}
                  getLabel={distributorLabel}
                  value={selectedDistributor}
                  onChange={setSelectedDistributor}
                  placeholder="All Distributors"
                  noun="distributors"
                  aria-label="Filter by distributor"
                />
              </div>
            </div>

            {/* Overdue Only */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Overdue only</span>
            </label>

            {/* Export */}
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Section 1: AR Enabled Accounts */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-green-50 border-b border-green-200">
                <h2 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  AR Enabled Accounts ({arEnabledAccounts.length})
                </h2>
                <p className="text-sm text-green-600 mt-1">Accounts with AR billing enabled</p>
              </div>
              {arEnabledAccounts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No AR-enabled accounts found
                </div>
              ) : (
                <div className="data-table-container">
                  <div className="data-table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="cursor-pointer" onClick={() => toggleSort('physicianName')}>
                            <div className="flex items-center gap-1">
                              Physician
                              {sortField === 'physicianName' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th>Distributor</th>
                          <th className="text-right">Credit Limit</th>
                          <th className="cursor-pointer text-right" onClick={() => toggleSort('outstandingBalance')}>
                            <div className="flex items-center gap-1 justify-end">
                              Outstanding
                              {sortField === 'outstandingBalance' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th className="text-right">Available</th>
                          <th className="text-center">Terms</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {arEnabledAccounts.map((physician) => (
                          <tr
                            key={physician.physicianId}
                            className={`cursor-pointer hover:bg-gray-50 ${physician.isOverLimit ? 'bg-red-50' : ''}`}
                            onClick={() => openDetailModal(physician)}
                          >
                            <td className="cell-primary" style={{ maxWidth: '200px' }}>
                              <div className="cell-stack">
                                <span className="cell-stack-value break-words">{physician.physicianName}</span>
                                {physician.clinicName && (
                                  <span className="cell-stack-label break-words">{physician.clinicName}</span>
                                )}
                                <span className="cell-stack-label cell-truncate">{physician.physicianEmail}</span>
                              </div>
                            </td>
                            <td className="cell-secondary">
                              {physician.distributorName || '-'}
                            </td>
                            <td className="cell-mono text-right">
                              {physician.creditLimit !== null ? formatCurrency(physician.creditLimit) : 'Unlimited'}
                            </td>
                            <td className="cell-mono text-right font-medium">
                              {formatCurrency(physician.outstandingBalance)}
                            </td>
                            <td className="cell-mono text-right text-green-600">
                              {physician.availableCredit !== null ? formatCurrency(physician.availableCredit) : 'Unlimited'}
                            </td>
                            <td className="cell-muted text-center">
                              {physician.paymentTerms.replace('net', 'Net ')}
                            </td>
                            <td className="text-center">
                              {physician.isOverLimit ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Over Limit
                                </span>
                              ) : physician.outstandingBalance > 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Balance Due
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Good Standing
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Outstanding Balances */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-orange-50 border-b border-orange-200">
                <h2 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Outstanding Balances ({outstandingBalanceAccounts.length})
                </h2>
                <p className="text-sm text-orange-600 mt-1">All accounts with unpaid invoices - aging breakdown</p>
              </div>
              {outstandingBalanceAccounts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No outstanding balances found
                </div>
              ) : (
                <div className="data-table-container">
                  <div className="data-table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="cursor-pointer" onClick={() => toggleSort('physicianName')}>
                            <div className="flex items-center gap-1">
                              Physician
                              {sortField === 'physicianName' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th>Distributor</th>
                          <th className="text-center">AR Status</th>
                          <th className="cursor-pointer text-right" onClick={() => toggleSort('outstandingBalance')}>
                            <div className="flex items-center gap-1 justify-end">
                              Outstanding
                              {sortField === 'outstandingBalance' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th className="text-right">Current</th>
                          <th className="text-right">1-30</th>
                          <th className="text-right">31-60</th>
                          <th className="text-right">61-90</th>
                          <th className="cursor-pointer text-right" onClick={() => toggleSort('days90plus')}>
                            <div className="flex items-center gap-1 justify-end">
                              90+
                              {sortField === 'days90plus' && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                            </div>
                          </th>
                          <th className="text-center">Invoices</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outstandingBalanceAccounts.map((physician) => (
                          <tr
                            key={physician.physicianId}
                            className={`cursor-pointer hover:bg-gray-100 ${physician.aging.days90plus > 0 ? 'bg-red-50' : physician.aging.days61to90 > 0 ? 'bg-orange-50' : ''}`}
                            onClick={() => openDetailModal(physician)}
                          >
                            <td className="cell-primary" style={{ maxWidth: '200px' }}>
                              <div className="cell-stack">
                                <span className="cell-stack-value break-words">{physician.physicianName}</span>
                                {physician.clinicName && (
                                  <span className="cell-stack-label break-words">{physician.clinicName}</span>
                                )}
                                <span className="cell-stack-label cell-truncate">{physician.physicianEmail}</span>
                              </div>
                            </td>
                            <td className="cell-secondary">
                              {physician.distributorName || '-'}
                            </td>
                            <td className="text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                physician.arEnabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {physician.arEnabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="cell-mono text-right font-semibold">
                              {formatCurrency(physician.outstandingBalance)}
                            </td>
                            <td className="cell-mono text-right text-gray-600">
                              {physician.aging.current > 0 ? formatCurrency(physician.aging.current) : '-'}
                            </td>
                            <td className="cell-mono text-right text-yellow-600">
                              {physician.aging.days1to30 > 0 ? formatCurrency(physician.aging.days1to30) : '-'}
                            </td>
                            <td className="cell-mono text-right text-orange-600">
                              {physician.aging.days31to60 > 0 ? formatCurrency(physician.aging.days31to60) : '-'}
                            </td>
                            <td className="cell-mono text-right text-red-600">
                              {physician.aging.days61to90 > 0 ? formatCurrency(physician.aging.days61to90) : '-'}
                            </td>
                            <td className="cell-mono text-right text-red-700 font-medium">
                              {physician.aging.days90plus > 0 ? formatCurrency(physician.aging.days90plus) : '-'}
                            </td>
                            <td className="cell-mono text-center">
                              {physician.invoiceCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Physician Detail Modal */}
        {selectedPhysician && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedPhysician.physicianName}</h2>
                  <p className="text-sm text-gray-500">{selectedPhysician.clinicName || selectedPhysician.physicianEmail}</p>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                {/* AR Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Outstanding</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(selectedPhysician.outstandingBalance)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Credit Limit</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedPhysician.creditLimit !== null ? formatCurrency(selectedPhysician.creditLimit) : 'Unlimited'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Invoices</p>
                    <p className="text-lg font-bold text-gray-900">{selectedPhysician.invoiceCount}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">AR Status</p>
                    <p className={`text-lg font-bold ${selectedPhysician.arEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedPhysician.arEnabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>

                {/* Statements Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Consolidated Statements
                    </h3>
                  </div>

                  {/* Date Range Filter for Statement Generation */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                        <input
                          type="date"
                          value={statementPeriodStart}
                          onChange={(e) => setStatementPeriodStart(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                        <input
                          type="date"
                          value={statementPeriodEnd}
                          onChange={(e) => setStatementPeriodEnd(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Filter by</label>
                        <select
                          value={statementDateField}
                          onChange={(e) => setStatementDateField(e.target.value as 'invoiceDate' | 'shippedDate')}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="invoiceDate">Invoice Date</option>
                          <option value="shippedDate">Shipped Date</option>
                        </select>
                      </div>
                      {(statementPeriodStart || statementPeriodEnd) && (
                        <button
                          onClick={() => { setStatementPeriodStart(''); setStatementPeriodEnd(''); }}
                          className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Clear dates
                        </button>
                      )}
                      <div className="ml-auto">
                        <button
                          onClick={handlePreviewStatement}
                          disabled={previewLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {previewLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          {statementPeriodStart || statementPeriodEnd ? 'Preview Range' : 'Generate Statement'}
                        </button>
                      </div>
                    </div>
                    {!statementPeriodStart && !statementPeriodEnd && (
                      <p className="text-xs text-gray-400 mt-2">Leave dates empty to include all outstanding invoices and orders</p>
                    )}
                  </div>

                  {detailLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                      <p className="mt-2 text-gray-500">Loading statements...</p>
                    </div>
                  ) : physicianStatements.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No statements created yet</p>
                      <p className="text-sm text-gray-400 mt-1">Generate a statement to consolidate unpaid invoices</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {physicianStatements.map((statement) => (
                        <div
                          key={statement._id}
                          className={`border rounded-lg p-4 ${
                            statement.status === 'paid' ? 'bg-green-50 border-green-200' :
                            statement.status === 'voided' ? 'bg-gray-50 border-gray-200' :
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{statement.statementNumber}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  statement.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                                  statement.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                  statement.status === 'paid' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {statement.status.charAt(0).toUpperCase() + statement.status.slice(1)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                {statement.periodStart && statement.periodEnd && (
                                  <span>Period: {new Date(statement.periodStart).toLocaleDateString()} – {new Date(statement.periodEnd).toLocaleDateString()} | </span>
                                )}
                                {statement.invoiceCount} invoices | Due: {new Date(statement.dueDate).toLocaleDateString()}
                              </div>
                              {statement.wireTransferNumber && (
                                <div className="text-sm text-green-600 mt-1">
                                  Wire Transfer: {statement.wireTransferNumber}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-lg text-gray-900">
                                {formatCurrency(statement.totalAmount)}
                              </span>
                              <button
                                onClick={() => handlePrintStatement(statement)}
                                disabled={printLoading}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded print:hidden"
                                title="Print Statement"
                              >
                                <Printer className="h-4 w-4" />
                              </button>
                              {statement.status === 'draft' && (
                                <button
                                  onClick={() => handleSendStatement(statement)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Mark as Sent"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              )}
                              {(statement.status === 'draft' || statement.status === 'sent') && (
                                <>
                                  <button
                                    onClick={() => openPaymentModal(statement)}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                                    title="Record Payment"
                                  >
                                    <CreditCard className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => openVoidModal(statement)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Void Statement"
                                  >
                                    <Ban className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statement Preview Modal */}
        {showPreviewModal && statementPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Statement Preview</h2>
                  <p className="text-sm text-gray-500">{statementPreview.physicianName}</p>
                </div>
                <button
                  onClick={() => { setShowPreviewModal(false); setStatementPreview(null); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(statementPreview.totalAmount)}</p>
                    <p className="text-sm text-blue-600">Total Amount</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-700">{statementPreview.invoiceCount}</p>
                    <p className="text-sm text-gray-500">Invoices</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-700">{statementPreview.orderCount}</p>
                    <p className="text-sm text-gray-500">Orders</p>
                  </div>
                </div>

                {/* Existing Invoices */}
                {statementPreview.existingInvoices.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-900 mb-2">Existing Invoices ({statementPreview.existingInvoices.length})</h3>
                    <div className="border rounded-lg divide-y">
                      {statementPreview.existingInvoices.map((inv) => (
                        <div key={inv.invoiceId} className="px-4 py-2 flex items-center justify-between text-sm">
                          <span className="font-mono">{inv.invoiceNumber}</span>
                          <span className="text-gray-500">{inv.orderCount} orders</span>
                          <span className="font-medium">{formatCurrency(inv.totalAmount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Orphan Orders */}
                {statementPreview.orphanOrders.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-900 mb-2">Orders Without Invoices ({statementPreview.orphanOrders.length})</h3>
                    <p className="text-sm text-gray-500 mb-2">Invoices will be created for these orders</p>
                    <div className="border rounded-lg divide-y">
                      {statementPreview.orphanOrders.slice(0, 10).map((order) => (
                        <div key={order.orderId} className="px-4 py-2 flex items-center justify-between text-sm">
                          <span className="font-mono">{order.orderNumber}</span>
                          <span className="text-gray-500">{order.status}</span>
                          <span className="font-medium">{formatCurrency(order.totalAmount)}</span>
                        </div>
                      ))}
                      {statementPreview.orphanOrders.length > 10 && (
                        <div className="px-4 py-2 text-center text-sm text-gray-500">
                          ... and {statementPreview.orphanOrders.length - 10} more orders
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Aggregated Line Items */}
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Products Summary</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {statementPreview.aggregatedLineItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm text-gray-900">{item.productName}</td>
                            <td className="px-4 py-2 text-sm text-gray-500 text-right">{item.quantity}</td>
                            <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.unitPrice)}</td>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.totalPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-right">Subtotal</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(statementPreview.subtotal)}</td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-right">Shipping</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(statementPreview.shippingTotal)}</td>
                        </tr>
                        {(statementPreview.telemedNetworkFeeTotal ?? 0) > 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-right">Telemed visit fees</td>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(statementPreview.telemedNetworkFeeTotal ?? 0)}</td>
                          </tr>
                        )}
                        {(statementPreview.taxTotal ?? 0) > 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-right">Tax</td>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(statementPreview.taxTotal ?? 0)}</td>
                          </tr>
                        )}
                        <tr className="bg-blue-50">
                          <td colSpan={3} className="px-4 py-2 text-sm font-semibold text-gray-900 text-right">Total</td>
                          <td className="px-4 py-2 text-lg font-bold text-blue-600 text-right">{formatCurrency(statementPreview.totalAmount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => { setShowPreviewModal(false); setStatementPreview(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStatement}
                  disabled={creatingStatement}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingStatement ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Create Statement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Record Payment Modal */}
        {showPaymentModal && paymentStatement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Record Payment</h2>
                <p className="text-sm text-gray-500">{paymentStatement.statementNumber}</p>
              </div>

              <div className="p-6">
                <div className="bg-green-50 rounded-lg p-4 mb-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(paymentStatement.totalAmount)}</p>
                  <p className="text-sm text-green-600">Total Amount Due</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wire Transfer Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={wireTransferNumber}
                      onChange={(e) => setWireTransferNumber(e.target.value)}
                      placeholder="e.g., WT-123456"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Notes (Optional)
                    </label>
                    <textarea
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="Any notes about the payment..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => { setShowPaymentModal(false); setPaymentStatement(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  disabled={recordingPayment}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {recordingPayment ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  Record Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Void Statement Modal */}
        {showVoidModal && voidStatement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Void Statement</h2>
                <p className="text-sm text-gray-500">{voidStatement.statementNumber}</p>
              </div>

              <div className="p-6">
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800">
                    Voiding this statement will unlink all {voidStatement.invoiceCount} invoices.
                    They will become standalone invoices again.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Voiding <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    placeholder="Enter reason for voiding this statement..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => { setShowVoidModal(false); setVoidStatement(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVoidStatement}
                  disabled={voidingStatement || !voidReason.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {voidingStatement ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Ban className="h-4 w-4" />
                  )}
                  Void Statement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Modal - Wire Instructions */}
        {showPrintModal && printStatementPending && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Print Statement: {printStatementPending.statementNumber}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wire Transfer Instructions
                </label>
                <textarea
                  value={wireInstructions}
                  onChange={(e) => setWireInstructions(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={6}
                  placeholder="Enter wire transfer instructions (bank name, account number, routing number, etc.)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  These instructions will appear on the printed statement.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPrintModal(false);
                    setPrintStatementPending(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={executePrint}
                  disabled={printLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {printLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Printer className="h-4 w-4" />
                  )}
                  Print
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print-only Statement View - Outside print:hidden container */}
      {printStatement && (
        <div className="hidden print:block print:absolute print:top-0 print:left-0 print:right-0 print:bg-white print:z-[9999]">
            <div className="max-w-3xl mx-auto p-4 print:p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4 border-b pb-3">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">STATEMENT</h1>
                  <p className="text-gray-600 mt-1">{printStatement.statementNumber}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">Statement Date</p>
                  <p className="text-gray-600">{new Date(printStatement.createdAt).toLocaleDateString()}</p>
                  <p className="font-semibold text-gray-900 mt-2">Due Date</p>
                  <p className="text-gray-600">{new Date(printStatement.dueDate).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase mb-1">Bill To</h2>
                <p className="font-semibold text-gray-900">
                  {printStatement.physicianId?.profile?.firstName} {printStatement.physicianId?.profile?.lastName}
                </p>
                {printStatement.physicianId?.profile?.clinicName && (
                  <p className="text-gray-600">{printStatement.physicianId.profile.clinicName}</p>
                )}
                {printStatement.physicianId?.physicianData?.clinicAddress && (
                  <>
                    <p className="text-gray-600">{printStatement.physicianId.physicianData.clinicAddress.street}</p>
                    {printStatement.physicianId.physicianData.clinicAddress.street2 && (
                      <p className="text-gray-600">{printStatement.physicianId.physicianData.clinicAddress.street2}</p>
                    )}
                    <p className="text-gray-600">
                      {printStatement.physicianId.physicianData.clinicAddress.city}, {printStatement.physicianId.physicianData.clinicAddress.state} {printStatement.physicianId.physicianData.clinicAddress.zipCode}
                    </p>
                  </>
                )}
              </div>

              {/* Period */}
              <div className="mb-4 bg-gray-50 p-3 rounded text-xs">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Statement Period:</span>{' '}
                  {new Date(printStatement.periodStart).toLocaleDateString()} - {new Date(printStatement.periodEnd).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Invoices Included:</span> {printStatement.invoiceCount}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Payment Terms:</span> {printStatement.paymentTerms?.replace('net', 'Net ') || 'Net 30'}
                </p>
              </div>

              {/* Line Items Table */}
              <table className="w-full mb-4 text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2 font-semibold text-gray-700">Product</th>
                    <th className="text-right py-2 font-semibold text-gray-700">Qty</th>
                    <th className="text-right py-2 font-semibold text-gray-700">Unit Price</th>
                    <th className="text-right py-2 font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {printStatement.lineItems?.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-1 text-gray-900">{item.productName}</td>
                      <td className="py-1 text-right text-gray-600">{item.quantity}</td>
                      <td className="py-1 text-right text-gray-600">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-1 text-right text-gray-900">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end text-sm">
                <div className="w-56">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900">{formatCurrency(printStatement.subtotal)}</span>
                  </div>
                  {printStatement.shippingTotal > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-gray-900">{formatCurrency(printStatement.shippingTotal)}</span>
                    </div>
                  )}
                  {(printStatement.telemedNetworkFeeTotal ?? 0) > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Telemed visit fees</span>
                      <span className="text-gray-900">{formatCurrency(printStatement.telemedNetworkFeeTotal ?? 0)}</span>
                    </div>
                  )}
                  {(printStatement.taxTotal ?? 0) > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Tax</span>
                      <span className="text-gray-900">{formatCurrency(printStatement.taxTotal ?? 0)}</span>
                    </div>
                  )}
                  {/* Without this the statement a clinic receives shows a Total
                      Due below subtotal + shipping with nothing accounting for
                      the difference — the same gap the telemed fee left. */}
                  {(printStatement.volumeRewardDiscount ?? 0) > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Volume reward discount</span>
                      <span className="text-green-700">−{formatCurrency(printStatement.volumeRewardDiscount ?? 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-t-2 border-gray-300 font-bold">
                    <span className="text-gray-900">Total Due</span>
                    <span className="text-gray-900">{formatCurrency(printStatement.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Instructions */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Payment Instructions</h3>
                {wireInstructions ? (
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{wireInstructions}</p>
                ) : (
                  <p className="text-sm text-gray-600">
                    Please remit payment by wire transfer. Reference statement number {printStatement.statementNumber} in your payment.
                  </p>
                )}
                {wireInstructions && (
                  <p className="text-sm text-gray-500 mt-2">
                    Reference: {printStatement.statementNumber}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminAR;
